import { getApiBaseUrl } from './apiBaseUrl';

const USER_DATA_CACHE_TTL_MS = 15000;
const userDataResponseCache = new Map();
const userDataRequestInflight = new Map();

const buildUserDataCacheKey = (mobile, options = {}) => {
    const normalizedMobile = String(mobile || '').trim();
    const normalizedCompany = String(options.salaryCompanyName || '').trim().toUpperCase();
    const normalizedBracket = String(options.salaryBracket || '').trim().toUpperCase();
    const token = getUserAccessToken();
    return `${normalizedMobile}::${normalizedCompany}::${normalizedBracket}::${token}`;
};

const buildJsonResponse = (status, body, headers = {}) => {
    const responseBody = typeof body === 'string' ? body : JSON.stringify(body || {});
    return new Response(responseBody, {
        status,
        headers: {
            'Content-Type': 'application/json',
            ...headers
        }
    });
};

const cloneResponsePayload = async (response) => {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        return {
            kind: 'json',
            value: await response.json()
        };
    }

    return {
        kind: 'text',
        value: await response.text()
    };
};

export function compactUserForStorage(user) {
    if (!user || typeof user !== 'object') return user;

    const compacted = { ...user };

    if (compacted.latest_sanction_letter && typeof compacted.latest_sanction_letter === 'object') {
        compacted.latest_sanction_letter = {
            ...compacted.latest_sanction_letter,
            html: null
        };
    }

    if (compacted.credit_report && typeof compacted.credit_report === 'object') {
        const creditReport = { ...compacted.credit_report };
        if ('report_html' in creditReport) creditReport.report_html = null;
        if ('parsed_summary' in creditReport && creditReport.parsed_summary && typeof creditReport.parsed_summary === 'object') {
            creditReport.parsed_summary = { ...creditReport.parsed_summary };
        }
        compacted.credit_report = creditReport;
    }

    return compacted;
}

export function clearUserSession() {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('loan_application_saved_state');
    sessionStorage.clear();
}

export function getUserAccessToken() {
    return String(localStorage.getItem('token') || '').trim();
}

export function buildAuthHeaders(extraHeaders = {}) {
    const token = getUserAccessToken();
    return token
        ? { ...extraHeaders, Authorization: `Bearer ${token}` }
        : { ...extraHeaders };
}

export async function fetchUserDataWithAuth(mobile, options = {}) {
    const cacheKey = buildUserDataCacheKey(mobile, options);
    const cached = userDataResponseCache.get(cacheKey);
    if (cached && (Date.now() - cached.cachedAt) < USER_DATA_CACHE_TTL_MS) {
        return buildJsonResponse(cached.status, cached.body, cached.headers);
    }

    if (userDataRequestInflight.has(cacheKey)) {
        const inflight = await userDataRequestInflight.get(cacheKey);
        return buildJsonResponse(inflight.status, inflight.body, inflight.headers);
    }

    const baseUrl = getApiBaseUrl();
    const requestPromise = (async () => {
        const params = new URLSearchParams();
        params.set('mobile', String(mobile || '').trim());
        if (options.salaryCompanyName) {
            params.set('salaryCompanyName', String(options.salaryCompanyName || '').trim());
        }
        if (options.salaryBracket) {
            params.set('salaryBracket', String(options.salaryBracket || '').trim());
        }
        const response = await fetch(`${baseUrl}/api/user-data?${params.toString()}`, {
            headers: buildAuthHeaders()
        });

        const payload = await cloneResponsePayload(response);
        const headers = {
            'Content-Type': response.headers.get('content-type') || 'application/json'
        };
        const result = {
            status: response.status,
            ok: response.ok,
            body: payload.kind === 'json' ? payload.value : payload.value,
            headers
        };

        if (response.ok) {
            userDataResponseCache.set(cacheKey, {
                cachedAt: Date.now(),
                status: response.status,
                body: payload.kind === 'json' ? payload.value : payload.value,
                headers
            });
        }

        return result;
    })();

    userDataRequestInflight.set(cacheKey, requestPromise);

    try {
        const result = await requestPromise;
        return buildJsonResponse(result.status, result.body, result.headers);
    } finally {
        userDataRequestInflight.delete(cacheKey);
    }
}
